"use client";
import { Button, Skeleton, Typography } from "antd/lib";
import Marquee from "react-fast-marquee";
import useAxiosQuery from "@/hooks/useMyQuery";
import MyCard from "@/app/components/MyCard";
import { useTranslations } from "next-intl";
import type { IToken } from "@/models/token";
import Heading from "@/app/components/Heading";
import { memo, useMemo } from "react";
import { TextNumberChange } from "@/app/components/numberChange";
import { ProductPercentChange } from "@/app/components/products/PercentChange";

const PeopleAlsoWatch = ({ symbol, title = "People_Also_Watch" }: { symbol: string; title?: string }) => {
  const t = useTranslations("products");

  const { data } = useAxiosQuery<{ data?: { tokens?: IToken[] } }>(
    {
      url: "/v1/product/people-also-watch",
      params: {
        symbol,
        limit: 10,
      },
    },
    {
      queryKey: ["people-also-watch", symbol],
      refetchInterval: 15 * 1000,
    },
  );

  const tokens = data?.data?.tokens;

  const renderSkeleton = useMemo(
    () =>
      Array(10)
        .fill("")
        .map((_, i) => (
          <div key={i} className="min-w-[140px] flex-grow mr-2">
            <Skeleton active paragraph={{ rows: 1, width: "100%" }} />
          </div>
        )),
    [],
  );

  const renderTokens = useMemo(
    () =>
      tokens?.map((item, key) => (
        <Button
          key={key}
          className="w-full mr-2 h-[unset] group cursor-pointer p-3"
          style={{ minWidth: 200 }}
          type="default"
        >
          <div className="flex flex-col gap-1 w-full">
            <div className="flex justify-between">
              <div className="flex items-center gap-2 overflow-x-hidden">
                <img src={item?.logo_uri} alt="token" width={17} height={17} loading="lazy" />
                <div className="flex-grow">
                  <Typography.Text strong ellipsis={true} className="w-full m-0 text-start">
                    {item.symbol}
                  </Typography.Text>
                </div>
              </div>
              <div className="flex items-center gap-2 m-0 transition-all">
                <div className="whitespace-nowrap flex">
                  <ProductPercentChange
                    change={(item.price - (item.last_24h_price ?? 0)) / (item.last_24h_price ?? 1)}
                  />
                </div>
              </div>
            </div>
            <div className="overflow-x-hidden whitespace-nowrap flex-grow-0 text-left">
              <TextNumberChange strong ellipsis={true} value={item.price} prefix={true} />
            </div>
          </div>
        </Button>
      )),
    [tokens],
  );

  return (
    <MyCard title={<Heading title={t(title)} />}>
      <Marquee pauseOnHover gradient={false}>
        {!tokens ? renderSkeleton : <div className="flex">{renderTokens}</div>}
      </Marquee>
    </MyCard>
  );
};

export default memo(PeopleAlsoWatch);
